'use client';

import React from 'react';
import type {
  HygraphDealership,
  HygraphDealershipAddress,
  HygraphMetaTag
} from '~/contracts/common';
import { HygraphDealershipConfiguration } from '~/contracts/common/HygraphDealership';

export type ContextType = {
  dealership: HygraphDealership | null;
  dealershipId: string | null;
  dealershipMetaTag: HygraphMetaTag | null;
  hasInventory: boolean;
};

const defaultContext: ContextType = {
  dealership: null,
  dealershipId: null,
  dealershipMetaTag: null,
  hasInventory: true
};

const StaticContext = React.createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<ContextType>;

export function StaticContextProvider({ children, ...props }: OwnProps) {
  return (
    <StaticContext.Provider value={props}>{children}</StaticContext.Provider>
  );
}

StaticContextProvider.displayName = 'StaticContextProvider';

export default StaticContext;

export const generateStaticContext = (
  customState: Partial<ContextType>
): ContextType => ({
  ...defaultContext,
  ...customState
});

export function MockedStaticContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: Partial<ContextType> }>) {
  return (
    <StaticContext.Provider value={generateStaticContext(mockValue)}>
      {children}
    </StaticContext.Provider>
  );
}

export function useStaticContext() {
  return React.useContext(StaticContext);
}

export function useDealership() {
  return useStaticContext().dealership;
}

export function useDealershipAddress(): Partial<HygraphDealershipAddress> {
  return useDealership()?.address ?? {};
}

export function useDealershipServiceAddress(): Partial<HygraphDealershipAddress> {
  return useDealership()?.serviceAddress ?? {};
}

export function useDealershipDepartmentHours() {
  return useDealership()?.hours ?? [];
}

export function useDealershipId() {
  return useStaticContext().dealershipId;
}

export function useDealershipConfiguration(): Partial<HygraphDealershipConfiguration> {
  return useDealership()?.configuration ?? {};
}

export function useDealershipMetaTag() {
  return useStaticContext().dealershipMetaTag;
}

export function useDealershipPhones() {
  return useDealership()?.phone ?? [];
}

export function useDealershipServiceCoupons() {
  return useDealership()?.serviceCoupons ?? [];
}

export function useIsDealershipPage() {
  return useStaticContext().dealershipId != null;
}
